import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('company');
    setUser(null);
    setCompany(null);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    const savedCompany = localStorage.getItem('company');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      if (savedCompany) setCompany(JSON.parse(savedCompany));
      Promise.all([
        api.get('/auth/me').catch(e => { throw e }),
        api.get('/auth/company').catch(e => { throw e })
      ])
        .then(([userRes, companyRes]) => {
          setUser(userRes.data);
          setCompany(companyRes.data);
          localStorage.setItem('user', JSON.stringify(userRes.data));
          localStorage.setItem('company', JSON.stringify(companyRes.data));
        })
        .catch(() => {
          logout();
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [logout]);

  const refreshAuth = useCallback(async () => {
    try {
      const [userRes, companyRes] = await Promise.all([
        api.get('/auth/me'),
        api.get('/auth/company')
      ]);
      setUser(userRes.data);
      setCompany(companyRes.data);
      localStorage.setItem('user', JSON.stringify(userRes.data));
      localStorage.setItem('company', JSON.stringify(companyRes.data));
    } catch (e) {
      console.error('Failed to refresh auth', e);
    }
  }, []);

  const login = useCallback(async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    
    // Suporte a 2FA
    if (response.data.requires_2fa) {
      return response.data;
    }

    const { access_token, user: userData, company: companyData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('company', JSON.stringify(companyData));
    setUser(userData);
    setCompany(companyData);
    return { user: userData, company: companyData };
  }, []);

  const verify2FA = useCallback(async (tempUserId, code) => {
    const response = await api.post('/auth/login/verify-2fa', { temp_user_id: tempUserId, code });
    const { access_token, user: userData, company: companyData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('company', JSON.stringify(companyData));
    setUser(userData);
    setCompany(companyData);
    return { user: userData, company: companyData };
  }, []);

  const register = useCallback(async (data, referralCode = null) => {
    let url = '/auth/register';
    if (referralCode) url += `?referral_code=${referralCode}`;
    const response = await api.post(url, data);
    const { access_token, user: userData, company: companyData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('company', JSON.stringify(companyData));
    setUser(userData);
    setCompany(companyData);
    return response.data;
  }, []);

  const loginWithGoogle = useCallback(async (credential, referralCode = null) => {
    let url = '/auth/google';
    if (referralCode) url += `?referral_code=${referralCode}`;
    const response = await api.post(url, { credential });
    const { access_token, user: userData, company: companyData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('company', JSON.stringify(companyData));
    setUser(userData);
    setCompany(companyData);
    return { user: userData, company: companyData };
  }, []);


  return (
    <AuthContext.Provider value={{ user, company, loading, login, register, loginWithGoogle, logout, refreshAuth, verify2FA }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};